import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Search, X, Plus, Sparkles, Loader2, AlertTriangle, Check } from "lucide-react";

// SubcategorySelector — Labix reusable admin component.
// Props: value (array of subcategory slugs), onChange (fn(newSlugs))
//        businessCtx: { name, short_description, full_description, services, tags }
//        macroSlug: optional preselected macro slug (used as default when creating new)
// Behavior: search-first UX. Zero list on open (only chips of selected).

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });

const normalize = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Bounded Levenshtein — returns Infinity when > maxDist to short-circuit.
function levenshtein(a, b, maxDist = 2) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > maxDist) return maxDist + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > maxDist) return maxDist + 1;
    prev = cur;
  }
  return prev[b.length];
}

// True if any word in `text` is within `maxDist` of query token (typo tolerance for len>=4).
function fuzzyHit(text, q, maxDist = 1) {
  if (!q || q.length < 4) return false;
  for (const w of text.split(/\s+/)) {
    if (w.length >= 4 && levenshtein(q, w, maxDist) <= maxDist) return true;
  }
  return false;
}

export default function SubcategorySelector({ value = [], onChange, businessCtx = {}, macroSlug = null }) {
  const [taxonomy, setTaxonomy] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [autoResults, setAutoResults] = useState([]); // classified from businessCtx
  const [autoBest, setAutoBest] = useState(0);
  const [creating, setCreating] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const autoTimer = useRef(null);

  useEffect(() => {
    client.get("/admin/taxonomy").then(r => setTaxonomy(r.data)).catch(() => setTaxonomy({ macros: [], subcategories: [] }));
  }, []);

  const subsBySlug = useMemo(() => {
    const map = {};
    (taxonomy?.subcategories || []).forEach(s => { map[s.slug] = s; });
    return map;
  }, [taxonomy]);

  const selectedItems = useMemo(
    () => value.map(slug => subsBySlug[slug]).filter(Boolean),
    [value, subsBySlug]
  );

  // Local search (client-side)
  useEffect(() => {
    if (!taxonomy || !query.trim()) { setResults([]); return; }
    const q = normalize(query.trim());
    const scored = [];
    const subs = macroSlug
      ? taxonomy.subcategories.filter(s => s.macro === macroSlug)
      : taxonomy.subcategories;
    for (const s of subs) {
      const name = normalize(s.name);
      const slug = normalize(s.slug.replace(/-/g, " "));
      const aliases = (s.aliases || []).map(normalize);
      let sc = 0;
      if (name === q || slug === q || aliases.includes(q)) sc = 100;
      else if (name.startsWith(q) || slug.startsWith(q)) sc = 85;
      else if (aliases.some(a => a.startsWith(q))) sc = 80;
      else if (name.includes(q) || slug.includes(q)) sc = 65;
      else if (aliases.some(a => a.includes(q))) sc = 55;
      else if (q.length >= 4 && (fuzzyHit(name, q) || fuzzyHit(slug, q) || aliases.some(a => fuzzyHit(a, q)))) sc = 50;
      if (sc >= 40) scored.push({ ...s, _score: sc });
    }
    scored.sort((a, b) => b._score - a._score);
    setResults(scored.slice(0, 15));
  }, [query, taxonomy]);

  // Auto-classify from business context (debounced, LOCAL ONLY — no LLM call)
  useEffect(() => {
    if (autoTimer.current) clearTimeout(autoTimer.current);
    if (!businessCtx.name || businessCtx.name.length < 3) { setAutoResults([]); setAutoBest(0); return; }
    autoTimer.current = setTimeout(async () => {
      try {
        const r = await client.post("/admin/taxonomy/classify", businessCtx);
        const local = (r.data.local || []).filter(x => !value.includes(x.slug));
        const filtered = macroSlug ? local.filter(x => x.macro_slug === macroSlug) : local;
        setAutoResults(filtered);
        setAutoBest(r.data.best_score || 0);
      } catch { /* silent */ }
    }, 600);
    return () => autoTimer.current && clearTimeout(autoTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessCtx.name, businessCtx.short_description, JSON.stringify(businessCtx.services), JSON.stringify(businessCtx.tags)]);

  const toggle = (slug) => {
    if (value.includes(slug)) onChange(value.filter(s => s !== slug));
    else onChange([...value, slug]);
  };

  const askAI = async () => {
    setAiLoading(true); setAiOpen(true);
    try {
      const r = await client.post("/admin/taxonomy/classify", businessCtx);
      setAiData(r.data);
    } catch (e) { setAiData({ error: e?.response?.data?.detail || "Errore" }); }
    finally { setAiLoading(false); }
  };

  if (!taxonomy) return <div className="text-sm text-slate-400">Caricamento tassonomia…</div>;

  return (
    <div data-testid="subcategory-selector" className="space-y-3">
      {/* Selected chips */}
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="selected-subs">
          {selectedItems.map(s => (
            <span key={s.slug} className="inline-flex items-center gap-1.5 text-xs bg-[#F0FAF3] text-[#0E7C3D] border border-[#E1F3E8] rounded-full pl-3 pr-1 py-1">
              {s.name}
              <button onClick={() => toggle(s.slug)} className="hover:bg-white rounded-full p-0.5" aria-label={`Rimuovi ${s.name}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Auto-suggestions from context (local only) */}
      {autoResults.length > 0 && !query && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3" data-testid="auto-suggestions">
          <div className="text-[11px] uppercase tracking-[0.15em] text-amber-800 mb-2 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Suggeriti dalla descrizione
          </div>
          <div className="flex flex-wrap gap-1.5">
            {autoResults.slice(0, 6).map(r => (
              <button key={r.slug} onClick={() => toggle(r.slug)} className="text-xs bg-white border border-amber-300 hover:border-amber-500 rounded-full px-3 py-1">
                + {r.name} <span className="text-[10px] text-slate-400">({r.score})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* AI fallback — visible whenever the business has a name and local confidence is low OR no local match at all. */}
      {!query && businessCtx.name && businessCtx.name.length > 3 && (autoResults.length === 0 || autoBest < 60) && (
        <button
          onClick={askAI}
          data-testid="ask-ai-btn"
          className="inline-flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full px-3 py-1.5"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {autoResults.length === 0 ? "Nessun match locale — Chiedi all\u2019AI" : "Confidenza bassa — Chiedi all\u2019AI"}
        </button>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca una sottocategoria (es. dentista, vernici, spurgo…)"
          data-testid="sub-search-input"
          className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-[#E5E7EB] focus:border-[#12964A] focus:ring-2 focus:ring-[#F0FAF3] outline-none"
        />
      </div>

      {/* Results list */}
      {query && (
        <div className="border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid="sub-results">
          {results.length === 0 ? (
            <div className="p-4 text-center">
              <div className="text-sm text-slate-500 mb-3">Nessuna sottocategoria adatta trovata</div>
              <button
                onClick={() => setCreating(true)}
                data-testid="create-sub-btn"
                className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-4 py-2 text-sm"
              >
                <Plus className="w-4 h-4" /> Crea &quot;{query}&quot;
              </button>
            </div>
          ) : (
            <div className="max-h-[280px] overflow-y-auto divide-y divide-[#F1F5F9]">
              {results.map(r => {
                const macro = (taxonomy.macros || []).find(m => m.slug === r.macro);
                const selected = value.includes(r.slug);
                return (
                  <button
                    key={r.slug}
                    onClick={() => toggle(r.slug)}
                    data-testid={`sub-result-${r.slug}`}
                    className={`w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 ${selected ? "bg-[#F0FAF3]" : ""}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-[#1F2937] font-medium">{r.name}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-[0.1em]">{macro?.name}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400">{r._score}</span>
                      {selected && <Check className="w-4 h-4 text-[#12964A]" />}
                    </div>
                  </button>
                );
              })}
              <div className="p-2 text-center border-t border-[#F1F5F9]">
                <button onClick={() => setCreating(true)} className="text-xs text-slate-500 hover:text-[#12964A]">
                  <Plus className="w-3 h-3 inline" /> Nessuna adatta? Crea nuova
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {creating && (
        <CreateModal
          initialName={query}
          taxonomy={taxonomy}
          suggestedMacro={macroSlug || autoResults[0]?.macro_slug}
          onCancel={() => setCreating(false)}
          onCreated={(sub) => {
            setTaxonomy(t => ({ ...t, subcategories: [...t.subcategories, { slug: sub.slug, name: sub.name, macro: sub.macro, order: sub.order, aliases: sub.aliases }] }));
            onChange([...value, sub.slug]);
            setCreating(false); setQuery("");
          }}
        />
      )}

      {aiOpen && (
        <AIModal loading={aiLoading} data={aiData} taxonomy={taxonomy}
          onApply={(slugs) => { const merged = Array.from(new Set([...value, ...slugs])); onChange(merged); setAiOpen(false); }}
          onClose={() => setAiOpen(false)} />
      )}
    </div>
  );
}

function CreateModal({ initialName, taxonomy, suggestedMacro, onCancel, onCreated }) {
  const [name, setName] = useState(initialName || "");
  const [macro, setMacro] = useState(suggestedMacro || (taxonomy.macros[0]?.slug));
  const [aliases, setAliases] = useState("");
  const [similar, setSimilar] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (force = false) => {
    if (!name.trim() || !macro) return;
    setBusy(true); setErr(null);
    try {
      const r = await client.post("/admin/taxonomy/subcategories", {
        name: name.trim(),
        macro_slug: macro,
        aliases: aliases.split(",").map(a => a.trim()).filter(Boolean),
        force,
      });
      if (r.data.success) {
        onCreated({ ...r.data.subcategory });
      } else if (r.data.reason === "similar_exists") {
        setSimilar(r.data.similar || []);
      }
    } catch (e) { setErr(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-3xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="create-sub-modal">
        <div className="font-display font-bold text-xl mb-4">Nuova sottocategoria</div>
        <label className="block mb-3">
          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-1">Nome</div>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="new-sub-name" />
        </label>
        <label className="block mb-3">
          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-1">Macro categoria</div>
          <select value={macro} onChange={(e) => setMacro(e.target.value)} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="new-sub-macro">
            {(taxonomy.macros || []).map(m => <option key={m.slug} value={m.slug}>{m.name}</option>)}
          </select>
        </label>
        <label className="block mb-4">
          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-1">Alias (separati da virgola)</div>
          <input value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="es. sinonimo1, inglese, plurale" className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="new-sub-aliases" />
        </label>
        {similar.length > 0 && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 mb-4">
            <div className="flex items-center gap-2 text-amber-800 text-sm mb-2"><AlertTriangle className="w-4 h-4" /> Sottocategorie simili esistenti:</div>
            <ul className="text-sm text-slate-700 space-y-1">
              {similar.map(s => <li key={s.slug}>· <strong>{s.name}</strong> <span className="text-xs text-slate-400">({s.macro_name}, score {s.score})</span></li>)}
            </ul>
            <button onClick={() => submit(true)} className="mt-2 text-xs text-amber-800 underline" data-testid="force-create-btn">Crea comunque</button>
          </div>
        )}
        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="text-sm text-slate-500 px-4 py-2">Annulla</button>
          <button onClick={() => submit(false)} disabled={busy || !name.trim()} data-testid="save-new-sub" className="inline-flex items-center gap-2 bg-[#12964A] text-white rounded-full px-5 py-2 text-sm disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crea
          </button>
        </div>
      </div>
    </div>
  );
}

function AIModal({ loading, data, taxonomy, onApply, onClose }) {
  const ai = data?.ai_suggested;
  const slugs = ai?.sub_slugs || [];
  const macros = taxonomy.macros || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="ai-suggest-modal">
        <div className="font-display font-bold text-xl mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#12964A]" /> Suggerimento AI</div>
        <p className="text-xs text-slate-500 mb-4">Solo consultivo. Confermi tu prima di applicare.</p>
        {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Analisi in corso…</div>}
        {!loading && !ai && <div className="text-sm text-slate-500">Nessun suggerimento disponibile.</div>}
        {!loading && ai && (
          <>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">Macro categoria proposta</div>
            <div className="text-sm font-medium mb-3">{macros.find(m => m.slug === ai.macro_slug)?.name || ai.macro_slug}</div>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-400 mb-1">Sottocategorie proposte</div>
            <ul className="text-sm space-y-1 mb-3">
              {slugs.map(sl => {
                const s = taxonomy.subcategories.find(x => x.slug === sl);
                return <li key={sl}>· <strong>{s?.name || sl}</strong></li>;
              })}
            </ul>
            <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600 mb-4">
              <strong>Motivazione:</strong> {ai.reason || "—"}<br />
              <strong>Confidenza:</strong> {ai.confidence ?? "—"}%
            </div>
          </>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-slate-500 px-4 py-2">Chiudi</button>
          {!loading && slugs.length > 0 && (
            <button onClick={() => onApply(slugs)} className="bg-[#12964A] text-white rounded-full px-5 py-2 text-sm" data-testid="apply-ai-btn">Applica</button>
          )}
        </div>
      </div>
    </div>
  );
}
